import React from 'react';

import { Button, Center, ScrollArea, ScrollAreaProps } from '@mantine/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { StoreProductTile } from 'pages/Settings/SettingTabs/StoresSettingsTab/StoreProductDetails/StoreProductTile';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ShopifyService } from 'services/shopify-service';
import { SHOPIFY_STORE_PRODUCTS_QUERY_KEY } from 'shared/constants/query-keys';
import { ShopifyProductsPage } from 'shared/interfaces/ShopifyStore';

interface StoreProductDetailsProps extends ScrollAreaProps {
  storeId: string;
  isStoreConfigured: boolean;
}

interface InfiniteStoreProductsQueryParams {
  pageParam?: string;
}

export function StoreProductDetails({ storeId, isStoreConfigured, ...scrollAreaProps }: StoreProductDetailsProps) {
  const { t } = useTranslation();

  const getStoreProducts = async ({ pageParam }: InfiniteStoreProductsQueryParams): Promise<ShopifyProductsPage> => {
    if (isStoreConfigured) {
      const { products } = await ShopifyService.getEmbeddedStoreData(storeId);
      // We return all products at once when store is already embedded, so there is no next page
      return { products, nextPageCursor: undefined };
    } else {
      return await ShopifyService.getActiveStoreProductsPage(storeId, pageParam);
    }
  };

  const onStoreProdudctsQueryError = () => {
    NotificationService.showResult(false, 'FEED_BACK.SHOPIFY.FAILURE.PRODUCTS');
  };

  const infiniteStoreProductsQuery = useInfiniteQuery({
    queryKey: [SHOPIFY_STORE_PRODUCTS_QUERY_KEY, { shopifyStoreId: storeId, isStoreConfigured }],
    queryFn: getStoreProducts,
    getNextPageParam: (lastPage) => lastPage.nextPageCursor,
    onError: onStoreProdudctsQueryError
  });

  const ViewMoreButton = () => (
    <Button
      miw={100}
      mt="sm"
      disabled={!infiniteStoreProductsQuery.hasNextPage || infiniteStoreProductsQuery.isFetchingNextPage}
      loading={infiniteStoreProductsQuery.isFetchingNextPage}
      variant="outline"
      onClick={() => infiniteStoreProductsQuery.fetchNextPage()}>
      {t('APP.ACTIONS.VIEW_MORE')}
    </Button>
  );

  if (infiniteStoreProductsQuery.isInitialLoading) return <FullScreenLoader w="100%" h="100%" />;

  const infiniteProducts = (infiniteStoreProductsQuery.data?.pages || []).flatMap((page) => page.products);

  return (
    <ScrollArea offsetScrollbars h="100%" {...scrollAreaProps}>
      {[
        ...infiniteProducts.map((product, key: number) => {
          return (
            <StoreProductTile
              key={key}
              mb={10}
              title={product.title}
              imgUrl={product.images[0]?.url}
              variantCount={product.variants.length}
            />
          );
        }),
        <Center key={infiniteProducts.length}>{infiniteStoreProductsQuery.hasNextPage && <ViewMoreButton />}</Center>
      ]}
    </ScrollArea>
  );
}
